import { useTranslation } from 'react-i18next'
import { AlarmClock, Search, UserRound, X } from 'lucide-react'
import { NativeSelect } from '@/components/common/Field'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { activeFilterCount, NO_FILTERS, type QuickFilters, type Who } from '@/lib/boardFilters'
import { PRIORITY_COLUMNS } from '@/lib/boardColumns'
import { statusLabel } from '@/lib/ticketLabels'
import { cn } from '@/lib/utils'

const chip = (on: boolean) =>
  cn(
    'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-meta transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
    on ? 'border-primary bg-accent font-medium text-accent-foreground' : 'border-line-strong text-ink-body hover:bg-surface-subtle',
  )

/** Instant filters on the loaded cards (z8nrz7fy99); never changes the saved board. */
export function BoardFilterBar({
  filters,
  customers,
  shown,
  total,
  onChange,
}: {
  filters: QuickFilters
  customers: { id: string; name: string }[]
  shown: number
  total: number
  onChange: (f: QuickFilters) => void
}) {
  const { t } = useTranslation()
  const set = (patch: Partial<QuickFilters>) => onChange({ ...filters, ...patch })
  const whoChip = (w: Who, label: string) => (
    <button type="button" aria-pressed={filters.who === w} className={chip(filters.who === w)} onClick={() => set({ who: filters.who === w ? 'all' : w })}>
      {w === 'me' ? <UserRound className="size-3.5" aria-hidden /> : null}
      {label}
    </button>
  )
  const active = activeFilterCount(filters)

  return (
    <div role="search" aria-label={t('boards.filters.label')} className="mb-4 flex flex-wrap items-center gap-2">
      <div className="relative w-full sm:w-64">
        <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-placeholder" aria-hidden />
        <Input
          type="search"
          aria-label={t('boards.filters.search')}
          placeholder={t('boards.filters.search')}
          className="h-9 pl-9"
          value={filters.q}
          onChange={(e) => set({ q: e.target.value })}
        />
      </div>
      {whoChip('me', t('boards.filters.me'))}
      {whoChip('unassigned', t('boards.unassigned'))}
      <span className="mx-1 h-5 w-px bg-line" aria-hidden />
      {PRIORITY_COLUMNS.map((p) => (
        <button
          key={p}
          type="button"
          aria-pressed={filters.priorities.includes(p)}
          className={chip(filters.priorities.includes(p))}
          onClick={() => set({ priorities: filters.priorities.includes(p) ? filters.priorities.filter((x) => x !== p) : [...filters.priorities, p] })}
        >
          {p}
        </button>
      ))}
      <button type="button" aria-pressed={filters.atRisk} className={chip(filters.atRisk)} onClick={() => set({ atRisk: !filters.atRisk })}>
        <AlarmClock className="size-3.5" aria-hidden />
        {t('boards.filters.atRisk')}
      </button>
      {customers.length > 1 ? (
        <NativeSelect aria-label={t('boards.filters.customer')} className="h-9 w-48" value={filters.customer} onChange={(e) => set({ customer: e.target.value })}>
          <option value="">{t('boards.filters.allCustomers')}</option>
          {customers.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </NativeSelect>
      ) : null}
      <span className="ml-auto text-label text-ink-muted" aria-live="polite">
        {active ? t('boards.filters.showing', { shown, total }) : t('boards.filters.count', { count: total })}
      </span>
      {active ? (
        <Button variant="ghost" size="sm" onClick={() => onChange(NO_FILTERS)}>
          <X aria-hidden />
          {t('boards.filters.clear')}
        </Button>
      ) : null}
    </div>
  )
}

/** The bar shown while cards are selected (z8nrz7fy9b): one action applied to each card. */
export function BulkBar({
  count,
  moves,
  agents,
  meId,
  can,
  busy,
  onMove,
  onPriority,
  onAssign,
  onClear,
}: {
  count: number
  moves: string[]
  agents: { id: string; name: string }[]
  meId: string | null
  can: { update: boolean; assign: boolean }
  busy: boolean
  onMove: (status: string) => void
  onPriority: (p: string) => void
  onAssign: (id: string | null) => void
  onClear: () => void
}) {
  const { t } = useTranslation()
  return (
    <div
      role="toolbar"
      aria-label={t('boards.bulk.label', { count })}
      className="fixed bottom-6 left-1/2 z-30 flex -translate-x-1/2 flex-wrap items-center gap-2 rounded-xl border border-line bg-background px-4 py-2.5 shadow-xl"
    >
      <span className="pr-1 text-meta font-semibold text-ink">{t('boards.bulk.selected', { count })}</span>
      {can.update ? (
        <NativeSelect aria-label={t('boards.card.moveTo')} className="h-9 w-48" value="" disabled={busy || moves.length === 0} onChange={(e) => e.target.value && onMove(e.target.value)}>
          <option value="">{moves.length ? t('boards.card.moveTo') : t('boards.bulk.noCommonMove')}</option>
          {moves.map((s) => (
            <option key={s} value={s}>
              {statusLabel(s)}
            </option>
          ))}
        </NativeSelect>
      ) : null}
      {can.update ? (
        <NativeSelect aria-label={t('boards.card.priority')} className="h-9 w-36" value="" disabled={busy} onChange={(e) => e.target.value && onPriority(e.target.value)}>
          <option value="">{t('boards.card.priority')}</option>
          {PRIORITY_COLUMNS.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </NativeSelect>
      ) : null}
      {can.assign ? (
        <NativeSelect
          aria-label={t('boards.card.assign')}
          className="h-9 w-48"
          value=""
          disabled={busy}
          onChange={(e) => e.target.value && onAssign(e.target.value === '__none__' ? null : e.target.value)}
        >
          <option value="">{t('boards.card.assign')}</option>
          {meId ? <option value={meId}>{t('boards.card.me')}</option> : null}
          <option value="__none__">{t('boards.card.unassign')}</option>
          {agents
            .filter((a) => a.id !== meId)
            .map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
        </NativeSelect>
      ) : null}
      <Button variant="ghost" size="sm" onClick={onClear} disabled={busy}>
        <X aria-hidden />
        {t('boards.bulk.clear')}
      </Button>
    </div>
  )
}
